import BigNumber from 'bignumber.js';

export type NetApyInputs = {
  userSummary: any;   // shape from formatUserSummaryAndIncentives
  reserves: any[];    // Aave reserves list
};

export type NetApyResult = {
  netApyOnEquity: number | null;     // decimal (0.031 = 3.1%), null if not defined
  netApyOnSupplied: number | null;   // decimal
  components: {
    supplyYieldUSD: string;          // BigNumber.toString()
    borrowCostUSD: string;
    totalCollateralUSD: string;
    totalDebtUSD: string;
    equityUSD: string;
  };
  note?: string; // explanation when null
};

// Helpers
const RAY = new BigNumber('1e27');

function rayToDec(ray: BigNumber.Value): BigNumber {
  return new BigNumber(ray).div(RAY); // e.g. 32000000000000000000000000 -> 0.032
}

function safeBN(x: BigNumber.Value): BigNumber {
  return new BigNumber(x || 0);
}

export function computeNetApy({ userSummary, reserves }: NetApyInputs): NetApyResult {
  let supplyYieldUSD = new BigNumber(0);
  let borrowCostUSD = new BigNumber(0);

  for (const pos of userSummary.userReservesData || []) {
    const reserve = reserves.find(
      (r: any) => r.underlyingAsset?.toLowerCase() === pos.reserve?.underlyingAsset?.toLowerCase()
    );
    if (!reserve) continue;

    const price = safeBN(reserve.priceInUsd); // already decimal USD
    const supplyBal = safeBN(pos.underlyingBalance);         // in token units
    const varDebt   = safeBN(pos.variableBorrows);
    const stDebt    = safeBN(pos.stableBorrows);

    const supplyUSD = supplyBal.multipliedBy(price);
    const varDebtUSD = varDebt.multipliedBy(price);
    const stDebtUSD  = stDebt.multipliedBy(price);
    const totalDebtUSD = varDebtUSD.plus(stDebtUSD);

    // Supply yield
    const supplyRateDec = rayToDec(reserve.liquidityRate || 0);
    supplyYieldUSD = supplyYieldUSD.plus(supplyUSD.multipliedBy(supplyRateDec));

    // Borrow cost (choose/weight stable vs variable)
    const varRateDec = rayToDec(reserve.variableBorrowRate || 0);
    const stRateDec  = rayToDec(reserve.stableBorrowRate || 0);

    let effBorrowRate = new BigNumber(0);
    if (stDebtUSD.gt(0) && varDebtUSD.gt(0)) {
      const total = totalDebtUSD;
      const wVar = varDebtUSD.div(total);
      const wSt  = stDebtUSD.div(total);
      effBorrowRate = varRateDec.multipliedBy(wVar).plus(stRateDec.multipliedBy(wSt));
    } else if (stDebtUSD.gt(0)) {
      effBorrowRate = stRateDec;
    } else if (varDebtUSD.gt(0)) {
      effBorrowRate = varRateDec;
    } else {
      effBorrowRate = new BigNumber(0);
    }

    borrowCostUSD = borrowCostUSD.plus(totalDebtUSD.multipliedBy(effBorrowRate));
  }

  const totalCollateralUSD = safeBN(userSummary.totalCollateralUSD);
  const totalDebtUSD = safeBN(userSummary.totalBorrowsUSD);
  const equityUSD = totalCollateralUSD.minus(totalDebtUSD);

  const EPS = new BigNumber('1e-8');
  const res: NetApyResult = {
    netApyOnEquity: null,
    netApyOnSupplied: null,
    components: {
      supplyYieldUSD: supplyYieldUSD.toString(),
      borrowCostUSD: borrowCostUSD.toString(),
      totalCollateralUSD: totalCollateralUSD.toString(),
      totalDebtUSD: totalDebtUSD.toString(),
      equityUSD: equityUSD.toString(),
    },
  };

  // on equity
  if (equityUSD.gt(EPS)) {
    res.netApyOnEquity = supplyYieldUSD.minus(borrowCostUSD).div(equityUSD).toNumber();
  } else {
    res.note = 'Equity is zero or negative; Net APY on equity is undefined.';
  }

  // on supplied
  if (totalCollateralUSD.gt(EPS)) {
    res.netApyOnSupplied = supplyYieldUSD.minus(borrowCostUSD).div(totalCollateralUSD).toNumber();
  }

  return res;
}

// Helper for tests
export function toRay(dec: number): string {
  return new BigNumber(dec).multipliedBy('1e27').toFixed(0);
}

// Helper to format Net APY for UI display
export function formatNetApyForUI(netApy: number | null): string {
  if (netApy === null) return 'N/A';
  
  const percentage = netApy * 100;
  const sign = percentage >= 0 ? '+' : '';
  return `${sign}${percentage.toFixed(2)}%`;
}
